import type { EnhancementToggle } from "@/hooks/useEnhancementConfig";
import type { SitelinkItem } from "@/components/shared/SitelinksSection";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Shape of enhancement JSONB columns on ad_account_settings */
export interface AccountEnhancementData {
  image_enhancements?: Record<string, boolean>;
  video_enhancements?: Record<string, boolean>;
  carousel_enhancements?: Record<string, boolean>;
  collection_enhancements?: Record<string, "OPT_IN" | "OPT_OUT">;
  enhancements_catalog?: Record<string, boolean>;
  flexible_advantage_plus_creative?: boolean;
  sitelinks?: SitelinkItem[];
}

export interface ColumnMapping {
  column: string;
  type: string;
}

export type ToggleToColumnMapping = Record<string, ColumnMapping>;

// ---------------------------------------------------------------------------
// Shared constants
// ---------------------------------------------------------------------------

/** Maps format_key → ad_account_settings JSONB column name */
const FORMAT_TO_SETTINGS_COLUMN: Record<string, string> = {
  single_image: "image_enhancements",
  single_video: "video_enhancements",
  carousel: "carousel_enhancements",
  collection: "collection_enhancements",
  catalog: "enhancements_catalog",
};

// ---------------------------------------------------------------------------
// Account Settings conversions
// ---------------------------------------------------------------------------

/**
 * Converts ad_account_settings JSONB data → flat Record<string, boolean>
 * for EnhancementTogglesPanel.
 *
 * CRITICAL: Iterates through toggle definitions (canonical keys), NOT raw
 * JSONB keys. This ensures legacy/malformed keys are silently ignored.
 */
export function accountSettingsToToggleValues(
  data: AccountEnhancementData,
  toggles: EnhancementToggle[],
): Record<string, boolean> {
  const values: Record<string, boolean> = {};

  for (const toggle of toggles) {
    const { format_key, toggle_key } = toggle;

    if (format_key === "flexible") {
      values[toggle_key] = data.flexible_advantage_plus_creative === true;
      continue;
    }

    const columnName = FORMAT_TO_SETTINGS_COLUMN[format_key];
    if (!columnName) continue;

    const blob = data[columnName as keyof AccountEnhancementData];
    if (!blob || typeof blob !== "object") {
      values[toggle_key] = false;
      continue;
    }

    if (format_key === "collection") {
      values[toggle_key] = (blob as Record<string, string>)[toggle_key] === "OPT_IN";
    } else {
      values[toggle_key] = !!(blob as Record<string, boolean>)[toggle_key];
    }
  }

  return values;
}

/**
 * Converts flat toggle values → ad_account_settings JSONB structure.
 */
export function toggleValuesToAccountSettings(
  values: Record<string, boolean>,
  toggles: EnhancementToggle[],
): AccountEnhancementData {
  const grouped: Record<string, string[]> = {};
  for (const t of toggles) {
    if (!grouped[t.format_key]) grouped[t.format_key] = [];
    grouped[t.format_key].push(t.toggle_key);
  }

  const boolMap = (formatKey: string) =>
    Object.fromEntries(
      (grouped[formatKey] ?? []).map((k) => [k, values[k] === true]),
    ) as Record<string, boolean>;

  return {
    image_enhancements: boolMap("single_image"),
    video_enhancements: boolMap("single_video"),
    carousel_enhancements: boolMap("carousel"),
    enhancements_catalog: boolMap("catalog"),
    collection_enhancements: Object.fromEntries(
      (grouped.collection ?? []).map((k) => [k, values[k] ? "OPT_IN" as const : "OPT_OUT" as const]),
    ) as Record<string, "OPT_IN" | "OPT_OUT">,
    flexible_advantage_plus_creative: values.flexible_advantage_plus_creative === true,
  };
}

// ---------------------------------------------------------------------------
// Template conversions
// ---------------------------------------------------------------------------

/** Read template row/data object → Record<toggleKey, boolean> */
export function templateToToggleValues(
  templateData: Record<string, unknown>,
  toggles: EnhancementToggle[],
): Record<string, boolean> {
  const values: Record<string, boolean> = {};

  for (const toggle of toggles) {
    const { toggle_key, column_name, column_type } = toggle;

    if (column_type === "boolean") {
      values[toggle_key] = !!(templateData[column_name] ?? false);
    } else if (column_type === "opt_string") {
      values[toggle_key] = templateData[column_name] === "OPT_IN";
    } else if (column_type === "catalog_json") {
      const blob = templateData.catalog_enhancements;
      const jsonKey = toggle_key.replace(/^catalog_/, "");
      if (blob && typeof blob === "object" && !Array.isArray(blob)) {
        values[toggle_key] = !!(blob as Record<string, unknown>)[jsonKey];
      } else {
        values[toggle_key] = false;
      }
    }
  }

  return values;
}

/** Convert flat toggle values → template column updates */
export function toggleValuesToTemplateColumns(
  values: Record<string, boolean>,
  toggles: EnhancementToggle[],
): Record<string, unknown> {
  const columns: Record<string, unknown> = {};
  const catalogJson: Record<string, boolean> = {};

  for (const toggle of toggles) {
    const { toggle_key, column_name, column_type } = toggle;
    const val = values[toggle_key] ?? false;

    if (column_type === "boolean") {
      columns[column_name] = val;
    } else if (column_type === "opt_string") {
      columns[column_name] = val ? "OPT_IN" : "OPT_OUT";
    } else if (column_type === "catalog_json") {
      const jsonKey = toggle_key.replace(/^catalog_/, "");
      catalogJson[jsonKey] = val;
    }
  }

  columns.catalog_enhancements = catalogJson;
  return columns;
}

// ---------------------------------------------------------------------------
// Dynamic mapping builder (for backward compat)
// ---------------------------------------------------------------------------

export function buildToggleToColumnMapping(
  toggles: EnhancementToggle[],
): ToggleToColumnMapping {
  const mapping: ToggleToColumnMapping = {};
  for (const def of toggles) {
    mapping[def.toggle_key] = { column: def.column_name, type: def.column_type };
  }
  return mapping;
}
